import React, { Fragment, useEffect, } from 'react';
import { Button, } from 'reactstrap';
import { sortBy, isEmpty, groupBy, get, pick, omit, uniqBy, keyBy, } from 'lodash';
import { toast, } from 'react-toastify';
import { useCounter, useToggle, } from 'react-use';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import numeral from 'numeral';
import { jsPDF } from 'jspdf';
import { PDFDocument, } from 'pdf-lib';
import fileDownload from 'js-file-download';

import firebase, { functions } from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import { drawAmazonProductLabel } from '../../label';
import { boxSizes, } from '../../shared/config';
import { batch, } from '../../shared/firebase';
import { labelString, adminFields, } from '../../shared/models/product';
import { cmSizes, } from '../../shared/models/amazonProduct';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import QuerySelector from '../QuerySelector';
import EditButton from '../EditButton';
import ProgressButton from '../ProgressButton';
import ModalButton from '../ModalButton';

const labelWidth = 52.5;
const labelHeight = 29.7;
const { keys, entries } = Object;
const checkFnsku = functions.httpsCallable('checkFnsku');
const printBarcode = functions.httpsCallable('printBarcode');
const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const productsRef = db.collectionGroup('products');
const inventoriesRef = db.collection('inventories');
const locationsRef = db.collection('locations');

export default AdminPage(function AdminNoFnskuLabelInventories (props) {
  const [version, { inc: updateVersion }] = useCounter(0);
  const locations = useCollectionSubscription(locationsRef.orderBy('sortKey', 'asc'));
  const locationsById = keyBy(locations, 'id');
  const inventories = useCollectionSubscription(inventoriesRef.where('status', '==', 'shelved').where('wasNoFnsku', '==', true).where('isNg', '==', false));
  const products = useDocumentsFetch(inventories.map(_ => db.collection('tenants').doc(_.tenantId).collection('products').doc(_.productId)), [inventories, version]);
  const productsByFullId = keyBy(products, _ => idWithTenant(_.ref));
  const items = inventories.map((inventory) => {
    const { tenantId, productId, locationId, } = inventory;
    const product = productsByFullId[[tenantId, productId].join('-')];
    const location = locationsById[locationId];
    return {
      ...inventory,
      product,
      location,
    };
  }).filter(_ => _.product);
  const itemsGroupedByLocationId = groupBy(items, 'locationId');
  const rows = locations.flatMap((location) => {
    const items = itemsGroupedByLocationId[location.id] || [];
    const itemsGroupedByFullProductId = groupBy(items, _ => idWithTenant(_.product?.ref));
    return entries(itemsGroupedByFullProductId).map(([fullProductId, items]) => {
      const [{ product }] = items;
      return {
        location,
        product,
        items,
      };
    });
  });

  return (
    <div className="admin-receivingRequests container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>FNSKUラベルなし在庫</h4>
        </div>
        <div className="mt-4 overflow-scroll position-relative" style={{ zIndex: 0 }}>
          {
            products.length > 0 ? (
              <table className="table sticky-table table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th style={{ minWidth: 150 }}>棚</th>
                    <th style={{ minWidth: 150 }}>商品ID</th>
                    <th style={{ minWidth: 150 }}>FNSKU</th>
                    <th style={{ minWidth: 100 }}>数量</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    rows.map((row) => {
                      const { location, product, items, } = row;
                      const tenantId = product.ref.parent.parent.id;
                      const onClickCheckFnsku = async () => {
                        await checkFnsku({ tenantId, productId: product.id });
                        updateVersion();
                      };
                      const onClickPrintBarcode = async () => {
                        try {
                          const canvas = document.createElement('canvas');
                          drawAmazonProductLabel(canvas, labelWidth, labelHeight, product, false);
                          const imageData = canvas.toDataURL('image/jpeg', 0.2);
                          const doc = new jsPDF({
                            orientation: 'landscape',
                            format: [labelWidth, labelHeight]
                          });
                          doc.addImage(imageData, 'JPEG', 0, 0, labelWidth, labelHeight);
                          const pdfData = doc.output('datauri');
                          const allPdfDoc = await PDFDocument.create();
                          const pdfDoc = await PDFDocument.load(pdfData);
                          await Array(items.length).fill().reduce(async (x, _, i) => {
                            await x;

                            const [page] = await allPdfDoc.copyPages(pdfDoc, keys(pdfDoc.getPages()));
                            await allPdfDoc.addPage(page);
                          }, Promise.resolve());

                          const content = await allPdfDoc.saveAsBase64({ dataUri: true })
                          // DEV
                          // fileDownload(await allPdfDoc.save(), 'hoge.pdf');

                          await printBarcode({
                            type: 'receivementProduct',
                            title: labelString(product),
                            imageData: content,
                          });
                        } catch(e) {
                          console.error(e);
                          toast.error('失敗しました');
                        }
                      };
                      const onClickComplete = async () => {
                        if(!window.confirm('本当にラベル貼替完了しましたか？')) return;

                        await batch(db, items, (batch, inventory) => {
                          batch.update(inventory.ref, {
                            productBarcodeInfo: labelString(product),
                            productBarcodeInfoType: 'fnsku',
                          });
                        });
                      };

                      return (
                        <tr key={[location.id, product.id].join('')}>
                          <td>
                            {location.sortKey}
                          </td>
                          <td>
                            {idWithTenant(product.ref)}
                          </td>
                          <td>
                            {product.fnsku}
                          </td>
                          <td className="text-right">
                            {numeral(items.length).format()}
                          </td>
                          <td className="text-right text-nowrap">
                            <div className="d-flex gap-1">
                              <ProgressButton process={onClickCheckFnsku}>
                                FNSKU再取得
                              </ProgressButton>
                              <ProgressButton process={onClickPrintBarcode} disabled={isEmpty(product.fnsku)}>
                                FNSKUラベル発行
                              </ProgressButton>
                              <ProgressButton process={onClickComplete} disabled={isEmpty(product.fnsku)}>
                                貼替完了
                              </ProgressButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                No Data
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});

